/*
 * With this generic you can create a config type and instantly generate an
 * options type that is a sparse version of the config type. The assign and
 * update functions are then a convenient way to copy from the options to the
 * config.
 */
export type OptionalProperties<T extends object> = {
    // Note that there's a gap here in that T[P] could be an array, but in
    // practice we don't ever want or need that for these options.
    [P in keyof T]?: T[P] extends object ? OptionalProperties<T[P]> : T[P];
}

/**
 * Similar to Object.assign, but works recursively on the arguments. This is
 * not fully general; it treats arrays as primitive values and does not recurse
 * over their values.
 */
export const assign = <T extends object>(
    target: T, ...sources: OptionalProperties<T>[]): T =>
{
    for(const source of sources) {
        if(!source) continue;

        for(const key in source) {
            const value = source[key];

            // I don't love the type casting here, but pragmatically, I think
            // it'll be fine and the type checking works as desired otherwise.
            if(value && typeof value == 'object' && !Array.isArray(value)) {
                if(!(key in target)) target[key] = {} as any;
                assign(target[key] as object, source[key] as object);
            } else {
                target[key] = source[key] as any;
            }
        }
    }
    return target;
}

/**
 * Uses the assign procedure to clone an object by assigning it to {}. The type
 * signature of `assign` itself makes this awkward, so the clone procedure does
 * a type cast to streamline it.
 */
export const clone = <T extends object>(source: T): T => assign({} as T, source);

/**
 * This is similar to the merge procedure above, but it will not add keys to
 * the target, only update keys that already exist in the target.
 */
export const update = <T extends object>(
    target: T, ...sources: OptionalProperties<T>[]): T =>
{

    for(const source of sources) {
        if(!source) continue;

        for(const key in target) {
            if(!(key in source)) continue;

            const value = source[key];

            // We can only recurse if there's an object in the target to
            // recurse upon. If there isn't, then we copy a reference from the
            // source into the target.
            //
            // I don't love the type casting here, but pragmatically, I think
            // it'll be fine and the type checking works as desired otherwise.
            if(value && typeof value == 'object' && target[key] && !Array.isArray(value)) {
                update(target[key] as object, source[key] as object);
            } else {
                target[key] = source[key] as any;
            }
        }
    }
    return target;
}
